/**
 * Custom web component for rendering Lucide icons
 * Handles automatic rendering with retry logic and proper timing
 */
export class LucideIcon extends HTMLElement {
  private iconName: string | null = null;
  private retryCount = 0;
  private maxRetries = 50;
  private retryInterval: number | null = null;
  private observer: MutationObserver | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  static get observedAttributes() {
    return ['name', 'width', 'height', 'stroke', 'fill', 'stroke-width'];
  }

  connectedCallback() {
    // Start rendering process
    this.iconName = this.getAttribute('name');
    this.renderIcon();

    // Watch for attribute changes
    this.observer = new MutationObserver(() => {
      this.renderIcon();
    });

    this.observer.observe(this, {
      attributes: true,
      attributeFilter: [
        'name',
        'width',
        'height',
        'stroke',
        'fill',
        'stroke-width',
      ],
    });
  }

  disconnectedCallback() {
    // Cleanup
    if (this.retryInterval) {
      clearTimeout(this.retryInterval);
      this.retryInterval = null;
    }

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ) {
    if (name === 'name' && oldValue !== newValue) {
      this.iconName = newValue;
      this.retryCount = 0; // Reset retry count for new icon
    }
    this.renderIcon();
  }

  private renderIcon() {
    // Clear any existing retry
    if (this.retryInterval) {
      clearTimeout(this.retryInterval);
      this.retryInterval = null;
    }

    const lucideIcons = (window as any).lucideIcons;

    // If Lucide isn't loaded yet, retry
    if (!lucideIcons || !lucideIcons.createElement) {
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        this.retryInterval = window.setTimeout(() => this.renderIcon(), 100);
      }
      return;
    }

    // Check if we have an icon name
    if (!this.iconName) {
      this.shadowRoot!.innerHTML = '';
      return;
    }

    // Check if the icon exists in Lucide
    const iconData = lucideIcons[this.iconName];
    if (!iconData) {
      console.warn(`Lucide icon "${this.iconName}" not found`);
      this.shadowRoot!.innerHTML = '';
      return;
    }

    try {
      // Create the SVG element
      const svg = lucideIcons.createElement(iconData);

      if (!svg) {
        console.error(`Failed to create SVG for icon "${this.iconName}"`);
        return;
      }

      // Apply attributes
      const width = this.getAttribute('width') || '24';
      const height = this.getAttribute('height') || '24';
      const stroke = this.getAttribute('stroke') || 'currentColor';
      const fill = this.getAttribute('fill') || 'none';
      const strokeWidth = this.getAttribute('stroke-width') || '2';

      svg.setAttribute('width', width);
      svg.setAttribute('height', height);
      svg.setAttribute('stroke', stroke);
      svg.setAttribute('fill', fill);
      svg.setAttribute('stroke-width', strokeWidth);

      // Add styling to inherit color from parent
      svg.style.cssText = 'display: inline-block; vertical-align: middle;';

      // Clear shadow root and append the SVG
      this.shadowRoot!.innerHTML = '';
      this.shadowRoot!.appendChild(svg);

      // Success! Reset retry count
      this.retryCount = 0;
    } catch (error) {
      console.error(`Error rendering Lucide icon "${this.iconName}":`, error);

      // Retry on error
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        this.retryInterval = window.setTimeout(() => this.renderIcon(), 100);
      }
    }
  }
}

// Register the custom element
if (!customElements.get('lucide-icon')) {
  customElements.define('lucide-icon', LucideIcon);
}
